import baseAxios from 'axios';
import { getJWT } from '@bynder/js-access-token-lib';

import * as bynderAxios from './customAxios';
import {
	Account,
	Cart,
	Language,
	NavItem,
	TreatmentKeys,
	UserAccount,
	UserData,
	SearchResponse,
	SpotlightMediaSearchResponse,
	SpotlightMetapropertiesSearchResponse,
	BrandStoreAndStyleGuideResponse,
	SpotlightSearchParams,
	SearchResults,
	TypesOfSpotlightRequests,
	PortalHomepage,
	SpotlightMediaSearchInCollectionResponse,
	SpotlightCollectionsSearchResponse,
} from '../types';
import { getCookieValue } from './cookie';
import { isCollectionDetailView } from './windowLocation';

const defaultNavigationItemsOnError: NavItem[] = [
	{
		id: '1',
		url: '/',
		label: 'Home',
	},
	{
		id: '2',
		url: '/media/',
		label: 'Assets',
	},
	{
		id: '3',
		url: '/collections/',
		label: 'Collections',
	},
];

export const API = {
	getCurrentUser,
	getPortalInfo: (): Promise<Account> =>
		bynderAxios.get({ url: '/v7/portal/portals/current_portal/', jwt: true }),
	getPortalHomepage: (): Promise<PortalHomepage> =>
		bynderAxios.get({ url: '/v7/portal/homepage/', jwt: true }),
	getAbilities: (uuid: string) =>
		bynderAxios.get({ url: `/v7/menu/abilities?uuid=${uuid}`, jwt: true }),
	getAbilitiesPublic: (uuid: string) =>
		bynderAxios.get({ url: `/v7/menu/abilities_public?uuid=${uuid}` }),
	getImpersonate: (): Promise<boolean> => bynderAxios.get({ url: '/user/is_impersonating' }),
	getNavigation: (uuid: string): Promise<NavItem[]> => {
		return bynderAxios
			.get({ url: getMenuRequestUrl(uuid), jwt: true })
			.catch(() => defaultNavigationItemsOnError);
	},
	getLanguages: (uuid: string): Promise<Language[]> =>
		bynderAxios.get({ url: getLanguagesRequestUrl(uuid), jwt: true }),
	getBrandstoreCart: (): Promise<Cart> => bynderAxios.get({ url: '/user/brandstore_cart' }),

	getFeatureTreatment: (
		feature: TreatmentKeys,
		isCacheDisabled: boolean,
	): Promise<{ treatment: string }> => {
		return bynderAxios.get({
			url: `/v7/featuretreatments/treatment/${feature}`,
			config: {
				headers: isCacheDisabled ? bynderAxios.headersNoCache : {},
			},
			jwt: true,
		});
	},

	getAccounts: (input: string): Promise<UserAccount[]> => {
		return bynderAxios.get({ url: '/account/suggest/', config: { params: { term: input } } });
	},

	searchSpotlightAssets: (
		searchTerm: string,
		params?: { [key: string]: string },
	): Promise<SpotlightMediaSearchResponse | SpotlightMediaSearchInCollectionResponse> => {
		return bynderAxios.get({
			url: isCollectionDetailView() ? '/search/collection/' : '/search/spotlight/assets/',
			config: {
				params: { keyword: searchTerm, jsonv2: 'true', ...params },
			},
		});
	},

	searchSpotlightCollections: (
		searchTerm: string,
		params?: { [key: string]: string },
	): Promise<SpotlightCollectionsSearchResponse> => {
		return bynderAxios.get({
			url: `${window.location.origin}/v7/collection/api/v3/collections/spotlight`,
			config: {
				params: { searchTerm, ...params },
			},
		});
	},

	searchSpotlightMetaproperties: (
		searchTerm: string,
		params?: { [key: string]: string },
	): Promise<SpotlightMetapropertiesSearchResponse> => {
		return bynderAxios.get({
			url: '/search/spotlight/metaproperties/',
			config: {
				params: { keyword: searchTerm, jsonv2: 'true', ...params },
			},
		});
	},

	searchStyledGuidePages: (
		searchTerm: string,
		params?: { [key: string]: string },
	): Promise<BrandStoreAndStyleGuideResponse> => {
		return bynderAxios.get({
			url: '/search/spotlight/styleguide-pages/',
			config: {
				params: { keyword: searchTerm, jsonv2: 'true', ...params },
			},
		});
	},

	searchCollection: (
		searchTerm: string,
		params?: { [key: string]: string },
	): Promise<SearchResponse> => {
		return bynderAxios.get({
			url: '/search/collection/',
			config: {
				params: { keyword: searchTerm, jsonv2: 'true', ...params },
			},
		});
	},

	searchGuide: (searchTerm: string) => {
		return bynderAxios.get({
			url: '/v7/guidelines-search/documents/spotlight',
			config: {
				params: { searchTerm },
			},
			jwt: true,
		});
	},
};

export const getCurrentLocale = () => {
	return getCookieValue('DEFAULTLOCALE') || 'en_US';
};

const getLanguagesRequestUrl = (uuid: string) => {
	return `/v7/menu/languages?include_display_names=true&locale=${getCurrentLocale()}&uuid=${uuid}`;
};

const getMenuRequestUrl = (uuid: string) => {
	return `/v7/menu/menu?locale=${getCurrentLocale()}&uuid=${uuid}`;
};

/*
 * Use baseAxios to locally handle 401 status code response
 */
async function getCurrentUser(): Promise<UserData | null> {
	const jwtToken = await getJWT();

	return baseAxios
		.get('/v7/identity/users/current_user/', {
			headers: { ...bynderAxios.headersDefault, Authorization: 'Bearer ' + jwtToken },
			validateStatus: (status) =>
				baseAxios.defaults.validateStatus?.(status) || status === 401,
		})
		.then((response) => (response.status === 401 ? null : response.data))
		.catch(bynderAxios.onError);
}

export const getAPISpotlightCalls = (
	term: string,
	params: SpotlightSearchParams | undefined,
	canShowBrandguidelines: boolean,
) => [
	{
		key: TypesOfSpotlightRequests.Collections,
		call: API.searchSpotlightCollections(term, params).catch(() => ({
			results: [],
			count: 0,
		})),
	},
	{
		key: TypesOfSpotlightRequests.Metaproperties,
		call: API.searchSpotlightMetaproperties(term, params).catch(() => ({
			results: [],
			count: 0,
		})),
	},
	{
		// Deprecated product but still used by some clients
		key: TypesOfSpotlightRequests.StyleGuidePages,
		call: API.searchStyledGuidePages(term, params).catch(() => ({
			results: [],
			count: 0,
		})),
	},
	...(canShowBrandguidelines
		? [
				{
					key: TypesOfSpotlightRequests.Guidelines,
					call: API.searchGuide(term).catch(() => ({ results: [] })),
				},
		  ]
		: []),
];

export const handleSpotlightResults = (
	responses: {
		key: string;
		response: SpotlightMediaSearchResponse | SpotlightMediaSearchInCollectionResponse;
	}[],
	setSearchResults: (value: React.SetStateAction<SearchResults>) => void,
) => {
	return responses.forEach((APIresponse) => {
		const { key, response } = APIresponse;

		if (key === TypesOfSpotlightRequests.Metaproperties) {
			setSearchResults((prevResults) => ({
				...prevResults,
				[key]: (response as unknown as SpotlightMetapropertiesSearchResponse).results,
			}));
		} else {
			const responseAsMedia = response as SpotlightMediaSearchResponse;

			setSearchResults((prevResults) => ({
				...prevResults,
				[key]: {
					items: responseAsMedia.results,
					total: responseAsMedia.count,
				},
			}));
		}
	});
};
